import { IPhoneEvent } from './phone-events'

// Spots the events in the phone's calendar that are really occasions.
//
// A birthday or an anniversary put into a phone calendar is a yearly all-day
// event, which is the same thing Emrouz calls an occasion — except that Emrouz
// can keep it in the Jalaali or the hijri calendar, where it belongs, instead of
// on a Gregorian date it will drift away from. So the app offers to take it
// over. Nothing is adopted without being asked.
//
// Pure: no storage, no plugin, no DOM. The scan that feeds it and the dialog
// that acts on it live in services/event-adoption-scan.ts and the day page.

const DAY_MS = 24 * 60 * 60 * 1000

// A year apart, give or take the leap day. Two occurrences of the same event
// this far apart are a yearly series, whatever rule the phone wrote it with.
const YEAR_MIN = 364
const YEAR_MAX = 367

// Calendars whose events are yearly by their nature. iOS keeps one called
// Birthdays and fills it from the address book; Android's is per-account.
const BIRTHDAY_CALENDARS = [/birthday/i, /تولد/]

export interface ICandidate {
    id: string // the phone's own event id, shared by every occurrence
    title: string
    start: number // the first occurrence in the window, as the day to adopt
    calendar: string
}

export interface AdoptionFilter {
    // The app's own mirror calendars. Their contents came from Emrouz in the
    // first place, so offering them back would be a loop.
    skipCalendars: string[]
    // Events already adopted, and events the user has said to leave alone.
    skipIds: string[]
}

const isBirthdayCalendar = (name: string) => BIRTHDAY_CALENDARS.some(rule => rule.test(name))

// Whether the gaps between these starts are all about a year.
function yearlyGaps(starts: number[]): boolean {
    if (starts.length < 2) return false
    for (let i = 1; i < starts.length; i++) {
        const days = Math.round((starts[i] - starts[i - 1]) / DAY_MS)
        if (days < YEAR_MIN || days > YEAR_MAX) return false
    }
    return true
}

// The events in the given window that look like occasions.
//
// One entry per event, not per occurrence: a recurring event keeps one id
// across its occurrences, which is exactly what makes the series visible here.
export function candidates(events: IPhoneEvent[], filter: AdoptionFilter): ICandidate[] {
    const skipIds = new Set(filter.skipIds)
    const skipCalendars = new Set(filter.skipCalendars)

    const groups = new Map<string, IPhoneEvent[]>()
    for (const e of events) {
        // Emrouz's own events are not the phone's to give back.
        if (e.eventId) continue
        if (skipIds.has(e.id)) continue
        if (skipCalendars.has(e.calendar)) continue
        // An occasion is a day, never an hour. Anything with a clock on it is
        // an appointment, however often it repeats.
        if (!e.allDay) continue
        const list = groups.get(e.id)
        if (list) list.push(e)
        else groups.set(e.id, [e])
    }

    const out: ICandidate[] = []
    for (const [id, list] of groups) {
        const sorted = [...list].sort((a, b) => a.start - b.start)
        const first = sorted[0]
        // A birthday calendar says what it is; anything else has to show a
        // year between one occurrence and the next.
        const yearly = isBirthdayCalendar(first.calendar) || yearlyGaps(sorted.map(e => e.start))
        if (!yearly) continue
        out.push({ id, title: first.title, start: first.start, calendar: first.calendar })
    }
    return out.sort((a, b) => a.start - b.start)
}

// Whether one row on screen is a candidate, which is what puts the mark on it.
export const isCandidate = (list: ICandidate[], event: IPhoneEvent) =>
    !event.eventId && list.some(c => c.id === event.id)
